import { ButtonPrimary } from "components/Clickable/Buttons";
import { ErrorMessage } from "components/Input/ErrorMessage";
import { PasswordInputGroup, TextInputGroup } from "components/Input/Input";
import { writeCMSDataClientSide } from "helpers/api";
import { basicEmailPattern, processServerSideErrors } from "helpers/forms";
import urls from "helpers/urls";
import { FormBase } from "interfaces";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "pages/account/auth/account-auth.module.scss";
import { SubmitHandler, useForm } from "react-hook-form";

interface Form extends FormBase {
    email: string;
    password: string;
}

export const SignInForm = () => {
    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<Form>();
    const router = useRouter();

    const onSubmit: SubmitHandler<Form> = async (data) => {
        const response = await writeCMSDataClientSide(
            "POST",
            `/api/auth/login`,
            {},
            data,
        );
        if (response.ok) {
            if (router.query.next || router.asPath === urls.dealerSignIn) {
                window.location.href = `${
                    router.query.next || urls.dealerSignIn
                }`;
            } else {
                window.location.pathname = urls.accountOverview;
            }
        } else if (response.status === 401) {
            setError("server", {
                type: "server",
                message: "The username or password you have entered is invalid",
            });
        } else {
            processServerSideErrors(response, data, setError);
        }
    };

    return (
        <>
            <h2 className="h4">Sign In</h2>
            <form
                noValidate
                onSubmit={(event) => {
                    /**
                     * Server-side errors need cleared before you can submit again
                     * https://github.com/react-hook-form/react-hook-form/issues/70
                     */
                    clearErrors();
                    handleSubmit(onSubmit)(event);
                }}
                className={styles.form}
            >
                <TextInputGroup
                    {...register("email", {
                        required: {
                            value: true,
                            message: "Enter your email address.",
                        },
                        pattern: basicEmailPattern,
                    })}
                    type="email"
                    name="email"
                    label={"Email"}
                    errorMessage={errors.email?.message}
                />
                <PasswordInputGroup
                    {...register("password", {
                        required: {
                            value: true,
                            message: "Enter your password.",
                        },
                    })}
                    name="password"
                    errorMessage={errors.password?.message}
                />
                <ButtonPrimary
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Sign In
                </ButtonPrimary>
            </form>
            {errors.server?.message && (
                <ErrorMessage>{errors.server.message}</ErrorMessage>
            )}
            <div className={styles.footerLinks}>
                <Link href={urls.register}>Don't have an account?</Link>
                <Link href={urls.forgotPassword}>Forgot Password</Link>
            </div>
        </>
    );
};
